import { tWithKeyAttr as t } from "../../i18nHelper";
import popupStyles from "./passwordVerificationPopup.module.css";
import doctor from "../../images/questionnaire_doctor.png";
import { IPopupWithoutProps } from "../../utils/types";
import { KeyboardEvent, ChangeEvent, useEffect, useRef, useState } from "react";
import Tooltip from "../tooltip/tooltip";
import { IVerificationData } from "../../services/types";
import {
  useLazyGetUserByPhoneNumberQuery,
  useValidateNumberMutation,
} from "../../services/api/api";
import { checkValidMail } from "../../utils/utils";
import { useAppSelector } from "../../services/hooks";
import { currentQuestionnaireSelector } from "../../services/selectors";

function PasswordVerificationPopup({
  onClose,
  phone,
  companyId,
  verificationData,
}: IPopupWithoutProps & {
  phone: string;
  companyId: number;
  verificationData: IVerificationData;
}) {
  const inputRefs = [
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
    useRef<HTMLInputElement>(null),
  ];
  const currentQuestionnaire = useAppSelector(currentQuestionnaireSelector);
  const initialState = ["", "", "", ""];
  const [nums, setNums] = useState(initialState);
  const [showSendCodeTooltip, setShowSendCodeTooltip] = useState(false);
  const [currentMail, setCurrentMail] = useState<string>("");
  const [validateMessage, setValidateMessage] = useState<string>("");
  const [successsMessage, setSuccessMessage] = useState<string>("");
  const [invalidEmail, setInvalidEmail] = useState<string>("");

  const [validateNumber] = useValidateNumberMutation();
  const [getUserByPhoneNumber] = useLazyGetUserByPhoneNumberQuery();

  useEffect(() => {
    if (nums.every((num) => num !== "")) {
      handleSubmit();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nums]);

  // Function to focus on the next input
  const focusNextInput = (index: number) => {
    const nextIndex = index + 1;
    const nextInputRef = inputRefs[nextIndex];
    if (nextInputRef && nextInputRef.current) {
      nextInputRef.current.focus();
    }
  };

  const handleNumChange = async (
    e: ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    focusNextInput(index);
    const limit = 1;
    try {
      await setNums(
        nums.map((num, index) =>
          index.toString() === e.target.id ? e.target.value?.slice(-limit) : num
        )
      );
    } catch (err) {}
  };

  const sendCodeAgain = () => {
    getUserByPhoneNumber({
      phone,
      companyId: -1,
      questionnaireId: currentQuestionnaire?.Id,
    }).then((res) => {
      if ("data" in res && res.data) {
        setShowSendCodeTooltip(true);
      }
    });
    setTimeout(() => {
      setShowSendCodeTooltip(false);
    }, 1500);
  };

  const handleSubmit = () => {
    if (!verificationData.isMail || checkValidMail(currentMail)) {
      setInvalidEmail("");
      setValidateMessage("");
      const code = nums.join("");
      const body = {
        CompanyId: companyId,
        Phone: phone,
        Code: code,
        Mail: verificationData.isMail ? currentMail : null,
      };
      validateNumber({
        Type: 1,
        CompanyId: companyId,
        body: body,
      }).then((res) => {
        if ("data" in res) {
          if (res.data.success === "true") {
            setSuccessMessage(t('AUTHENTICATION_SUCCESS'));
            onClose();
          } else {
            setValidateMessage(t('THE_CODE_IS_INCORRECT'));
            setNums(Array(4).fill(""));
          }
        }
      });
    } else if (verificationData.isMail) {
      setInvalidEmail(t('INVALID_EMAIL_ADDRESS'));
    }
  };

  // Function to focus on the next input
  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, index: number) => {
    const { key } = e;
    const currentInput = inputRefs[index].current;
    if (!currentInput) return;

    if (key >= "0" && key <= "9") {
      e.preventDefault();
      const newNums = [...nums];
      newNums[index] = key;
      setNums(newNums);

      if (index < inputRefs.length - 1) {
        inputRefs[index + 1].current?.focus();
      }
    }

    if (key === "Backspace") {
      e.preventDefault();
      const newNums = [...nums];

      if (newNums[index]) {
        newNums[index] = "";
        setNums(newNums);
      } else if (index > 0) {
        newNums[index - 1] = "";
        setNums(newNums);
        inputRefs[index - 1].current?.focus();
      }
    }

    if (key === "ArrowRight") {
      if (index < inputRefs.length - 1) {
        inputRefs[index + 1].current?.focus();
      }
    }

    if (key === "ArrowLeft") {
      if (index > 0) {
        inputRefs[index - 1].current?.focus();
      }
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData("text").replace(/\D/g, ""); // Get only digits

    if (pasteData.length === 4) {
      const newOtp = pasteData?.split("")?.slice(0, 4);
      setNums([...newOtp]);

      // Focus last field to mimic correct user experience
      inputRefs[3].current?.focus();
    }
  };

  return (
    <article
      className={popupStyles.popup}
      data-testid="sms-chat-verification-popup"
    >
      <img src={doctor} alt="Docktor" className={popupStyles.popup__img} />
      <h2 className={popupStyles.popup__title}>{t('PLEASE_ENTER_VERIFICATION_CODE')}</h2>
      <p
        // className={popupStyles.popup__text}
        className={`${popupStyles.popup__text}`}
      >
        {t('SENT_VERIFICATION_CODE_SMS')}{phone?.slice(-3)}
      </p>
      <p className={popupStyles.popup__enterCodeLabel}>
        {t('IF_KOSHER_PHONE_VOICE_MSG')}{" "}
      </p>

      <ul className={popupStyles.popup__enterCode}>
        {nums.map((num, index) => (
          <input
            key={index}
            ref={inputRefs[index]}
            onChange={(e) => handleNumChange(e, index)}
            type="number"
            name={index.toString()}
            id={index.toString()}
            className={popupStyles.popup__number}
            placeholder="__"
            value={nums[index]}
            autoFocus={index === 0}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={handlePaste}
          />
        ))}
      </ul>
      {successsMessage ? (
        <p
          className={`${popupStyles.popup__success_message}`}
          data-testid="verification-success-message"
        >
          {successsMessage}
        </p>
      ) : (
        validateMessage && (
          <p
            className={`${popupStyles.popup__invalid_message}`}
            data-testid="verification-invalid-message">
            {validateMessage}
          </p>
        )
      )}

      <p className={popupStyles.popup__sendAgain}>
        {t('DID_NOT_RECEIVE_CODE')}<span
          className={`${popupStyles.popup__sendAgain_underlined} hover`}
          data-testid="send-code-again"
          onClick={sendCodeAgain}
        >
          {t('RESEND')}<Tooltip isShown={showSendCodeTooltip}>{t('SENT_AGAIN')}</Tooltip>
        </span>
      </p>
      {verificationData?.isMail && (
        <div>
          <label
            htmlFor="email"
            className={`${popupStyles.popup__email_label}`}
          >
            {t('EMAIL_ADDRESS_FOR_CONTACT')}</label>
          <div className={popupStyles.popup__email_wrap}>
            <input
              // ref={inputRefs[index]}
              onChange={(e) => setCurrentMail(e.target.value)}
              type="email"
              name={"email"}
              id={"email"}
              className={`${popupStyles.popup__email_input} ${
                invalidEmail && popupStyles.popup__email_input_invalid
              } `}
              placeholder={t('EMAIL_ADDRESS')}
              value={currentMail}
            />
            <button
              className={`${popupStyles.popup__nextBtn} hover`}
              type="button"
              onClick={handleSubmit}
            >
              {t('APPROVAL_AND_SENDING')}</button>
          </div>
          {invalidEmail && (
            <p className={`${popupStyles.popup__invalid_message}`}>
              {invalidEmail}
            </p>
          )}
        </div>
      )}
    </article>
  );
}

export default PasswordVerificationPopup;
